/**
 * Scadenze della piattaforma (ADR 0044), per gli amministratori.
 *
 * Le rotte sono di `tools` (prefisso del gateway `/api/v1/tools/deadlines`). L'elenco unisce
 * le voci scritte a mano (prove, abbonamenti, chiavi API, domini) e le connessioni di
 * piattaforma con una scadenza; delle connessioni di utenti e progetti arrivano solo i
 * numeri. Provvisorio dichiarato, come gli altri client: percorsi e tipi scritti a mano,
 * presidiati da `deadlines.test.ts`.
 */

import type { ApiClient } from './client';

/** Prefisso delle rotte. */
const DEADLINES = '/api/v1/tools/deadlines';

/** Tipi di una voce scritta a mano, nell'ordine del modulo. */
export const DEADLINE_KINDS = ['token', 'api_key', 'certificate', 'subscription', 'trial', 'domain', 'other'] as const;

/** Tipo di una voce. */
export type DeadlineKind = (typeof DEADLINE_KINDS)[number];

/** Urgenza rispetto a oggi. */
export type DeadlineUrgency = 'expired' | 'soon' | 'ok';

/** Una voce scritta a mano. */
export interface Deadline {
  /** Identificativo. */
  id: string;
  /** Titolo. */
  title: string;
  /** Tipo. */
  kind: DeadlineKind;
  /** Fornitore. */
  provider: string;
  /** Scadenza, ISO 8601. */
  expires_at: string;
  /** Giorni di preavviso. */
  warn_days: number;
  /** Pagina di rinnovo, vuota o `https://`. */
  url: string;
  /** Note. */
  notes: string;
  /** Urgenza calcolata dal servizio. */
  urgency: DeadlineUrgency;
  /** Chi l'ha scritta. */
  created_by: string;
  /** Chi l'ha cambiata per ultimo. */
  updated_by: string;
  /** Ultima modifica, ISO 8601. */
  updated_at: string;
}

/** Una connessione di piattaforma con scadenza o non valida. */
export interface ConnectionDeadline {
  /** Identificativo della connessione. */
  id: string;
  /** Fornitore. */
  provider: 'github' | 'google';
  /** Nome della connessione. */
  label: string;
  /** Account del fornitore. */
  account_login: string;
  /** Metodo di accesso. */
  auth_method: string;
  /** Stato all'ultima verifica. */
  status: 'active' | 'invalid' | 'expired';
  /** Scadenza dichiarata dal fornitore, ISO 8601; `null` se non ne ha. */
  expires_at: string | null;
  /** Urgenza. */
  urgency: DeadlineUrgency;
  /** Ultima verifica, ISO 8601. */
  last_checked_at: string;
}

/** Elenco della pagina. */
export interface DeadlinesList {
  /** Istante di riferimento delle urgenze. */
  now: string;
  /** Voci scritte a mano, dalla più vicina. */
  items: Deadline[];
  /** Connessioni di piattaforma. */
  connections: ConnectionDeadline[];
  /** Connessioni di utenti e progetti, solo contate. */
  owned_connections: {
    /** Scadute o non valide. */
    expired: number;
    /** In scadenza entro `window_days`. */
    expiring: number;
    /** Finestra del conteggio, in giorni. */
    window_days: number;
  };
}

/** Campi di una voce da scrivere o cambiare. */
export interface DeadlineInput {
  /** Titolo. */
  title: string;
  /** Tipo. */
  kind: DeadlineKind;
  /** Fornitore. */
  provider: string;
  /** Scadenza, ISO 8601 con fuso. */
  expires_at: string;
  /** Giorni di preavviso. */
  warn_days: number;
  /** Pagina di rinnovo. */
  url: string;
  /** Note. */
  notes: string;
}

/** Operazioni sulle scadenze, esposte alla UI. */
export class DeadlinesApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenco completo.
   *
   * @returns Voci, connessioni di piattaforma e conteggi.
   * @throws ApiError 403 per chi non è amministratore.
   */
  list(): Promise<DeadlinesList> {
    return this.client.request<DeadlinesList>('GET', DEADLINES);
  }

  /**
   * Scrive una voce nuova.
   *
   * @param body Campi.
   * @returns La voce scritta.
   */
  create(body: DeadlineInput): Promise<Deadline> {
    return this.client.request<Deadline>('POST', DEADLINES, body);
  }

  /**
   * Sostituisce i campi di una voce.
   *
   * @param id Identificativo.
   * @param body Campi nuovi.
   * @returns La voce aggiornata.
   */
  update(id: string, body: DeadlineInput): Promise<Deadline> {
    return this.client.request<Deadline>('PUT', `${DEADLINES}/${encodeURIComponent(id)}`, body);
  }

  /**
   * Cancella una voce.
   *
   * @param id Identificativo.
   */
  remove(id: string): Promise<void> {
    return this.client.request<void>('DELETE', `${DEADLINES}/${encodeURIComponent(id)}`);
  }
}
